<div class="space-y-6">
  <app-page-header [backTo]="['/permissions']" backLabel="Retour aux permissions" />

  @if (loadError() || loading()) {
    <app-data-state [loading]="loading()" [error]="loadError()" (retry)="fetch()" />
  } @else if (permission(); as p) {
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="min-w-0">
          <h1 class="font-mono text-title-sm font-bold text-gray-800 dark:text-white/90 sm:text-title-md">
            {{ p.name }}
          </h1>
          @if (p.displayName && p.displayName !== p.name) {
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ p.displayName }}</p>
          }
          <div class="mt-2 flex flex-wrap items-center gap-2">
            <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase text-gray-700 dark:bg-gray-800 dark:text-gray-300">
              type: {{ p.type || '—' }}
            </span>
            @if (p.decisionStrategy) {
              <span class="inline-flex items-center rounded-full bg-blue-light-50 px-2 py-0.5 text-[10px] font-medium text-blue-light-700 dark:bg-blue-light-500/15 dark:text-blue-light-400">
                {{ p.decisionStrategy }}
              </span>
            }
            @if (p.ownerManagedAccess) {
              <span class="inline-flex items-center rounded-full bg-warning-50 px-2 py-0.5 text-[10px] font-medium text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
                Owner-managed
              </span>
            }
          </div>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            (click)="openPolicies()"
            class="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            Gérer les policies
          </button>
          <button
            type="button"
            (click)="openDelete()"
            class="inline-flex items-center gap-2 rounded-lg border border-error-300 px-4 py-2 text-sm font-medium text-error-700 transition hover:bg-error-50 dark:border-error-800 dark:text-error-400 dark:hover:bg-error-500/10"
          >
            Supprimer
          </button>
        </div>
      </div>
    </section>

    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Scopes ciblés</h2>
        @if (p.scopes?.length) {
          <ul class="flex flex-wrap gap-1.5">
            @for (s of p.scopes; track s) {
              <li>
                <span class="inline-flex items-center rounded-md bg-brand-50 px-2 py-1 font-mono text-[11px] text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">{{ s }}</span>
              </li>
            }
          </ul>
        } @else {
          <p class="text-xs text-gray-500 dark:text-gray-400">Aucun scope.</p>
        }
      </section>

      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Politiques liées</h2>
        @if (p.policies?.length) {
          <ul class="space-y-1.5">
            @for (pol of p.policies; track pol) {
              <li class="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-2 text-sm dark:bg-gray-800">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" class="text-gray-400">
                  <path fill-rule="evenodd" d="M10 1.667a4.583 4.583 0 0 0-4.583 4.583v1.667h-.834a2.083 2.083 0 0 0-2.083 2.083v6.667c0 1.15.933 2.083 2.083 2.083h10.834c1.15 0 2.083-.933 2.083-2.083v-6.667a2.083 2.083 0 0 0-2.083-2.083h-.834V6.25A4.583 4.583 0 0 0 10 1.667Zm3.083 6.25V6.25a3.083 3.083 0 0 0-6.166 0v1.667h6.166Z" clip-rule="evenodd" />
                </svg>
                <span class="font-mono text-xs text-gray-700 dark:text-gray-300">{{ pol }}</span>
              </li>
            }
          </ul>
        } @else {
          <p class="text-xs text-gray-500 dark:text-gray-400">
            Aucune politique attachée. Cette permission est inerte jusqu'à ce qu'un rôle ou un user y soit lié.
          </p>
        }
      </section>

      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Identifiants Keycloak</h2>
        <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">ID</dt>
            <dd class="mt-1 break-all font-mono text-xs text-gray-700 dark:text-gray-300">{{ p.id || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Resource type</dt>
            <dd class="mt-1 text-sm text-gray-700 dark:text-gray-300">{{ p.resourceType || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Icon URI</dt>
            <dd class="mt-1 break-all text-xs text-gray-700 dark:text-gray-300">{{ p.icon_uri || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Owner</dt>
            <dd class="mt-1 text-sm text-gray-700 dark:text-gray-300">{{ p.owner || '—' }}</dd>
          </div>
        </dl>
      </section>
    </div>
  }
</div>

<!-- Manage policies modal -->
<app-modal [isOpen]="policiesOpen()" (close)="closePolicies()" className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-gray-dark">
  <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Policies attachées</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    Chaque policy ajoute une règle qui autorise (ou non) l'accès. Les modifications sont appliquées immédiatement.
  </p>

  <div class="mt-4 space-y-1.5">
    @if (permission()?.policies?.length) {
      @for (pol of permission()!.policies; track pol) {
        <div class="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-800">
          <span class="min-w-0 truncate font-mono text-xs text-gray-700 dark:text-gray-300">{{ pol }}</span>
          <button
            type="button"
            (click)="removePolicy(pol)"
            [disabled]="policyRemoving() !== null"
            class="text-xs font-medium text-error-600 hover:text-error-700 disabled:opacity-40 dark:text-error-400"
            title="Retirer"
          >
            @if (policyRemoving() === pol) { … } @else { Retirer }
          </button>
        </div>
      }
    } @else {
      <p class="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">Aucune policy attachée.</p>
    }
  </div>

  <div class="mt-4 border-t border-gray-200 pt-4 dark:border-gray-800">
    <label for="add-policy" class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-400">Ajouter une policy</label>
    <div class="flex gap-2">
      <input
        id="add-policy"
        type="text"
        [formControl]="newPolicyCtrl"
        placeholder="Nom de la policy (ex. ROLE_ADMIN-policy)"
        (keyup.enter)="addPolicy()"
        class="h-10 flex-1 rounded-lg border border-gray-300 bg-transparent px-3 font-mono text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
      />
      <button
        type="button"
        (click)="addPolicy()"
        [disabled]="policyAdding() || newPolicyCtrl.invalid"
        class="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-brand-300"
      >
        @if (policyAdding()) { … } @else { Ajouter }
      </button>
    </div>
  </div>

  @if (policyError(); as msg) {
    <div class="mt-3 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">{{ msg }}</div>
  }

  <div class="mt-6 flex justify-end">
    <button type="button" (click)="closePolicies()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Fermer</button>
  </div>
</app-modal>

<app-confirm-modal
  [open]="deleteOpen()"
  [busy]="deleting()"
  [error]="deleteError()"
  title="Supprimer cette permission ?"
  confirmLabel="Supprimer définitivement"
  busyLabel="Suppression…"
  danger
  (confirm)="confirmDelete()"
  (close)="closeDelete()"
>
  <span class="font-mono font-medium text-gray-700 dark:text-gray-300">{{ permission()?.name }}</span> sera retiré de Keycloak. Les rôles qui en dépendent perdront l'accès au resource/scope correspondant.
</app-confirm-modal>
